import * as a from '@mitranim/js/all.mjs'
import * as dr from '@mitranim/js/dom_reg.mjs'
import {A, E} from './util.mjs'
import * as d from './dat.mjs'
import * as em from './elem-misc.mjs'
import * as ei from './elem-input.mjs'
import * as eff from './elem-form-field.mjs'
import * as eir from './elem-input-radio.mjs'
import * as s from './views/svg.mjs'

/*
Represents a non-boolean checkbox. Suitable for multiselects. Not suitable for
booleans; see `FormFieldBool` for that.

TODO consolidate with `FormChoice` (requires us to convert React-based forms to
custom-element-based forms).

TODO: maybe this should not always subclass fieldset. This can lead to nested
fieldsets, for example when checkbox fields are included in a multiselect.
Maybe this should be a mixin, to allow different superclasses.
*/
export class FormFieldCheckbox extends eff.FormField {
  static {dr.reg(this)}
  get Input() {return CheckboxInput}
  get Label() {return CheckboxLabel}
  get A() {return A.cls(`form-choice`)}
}

/*
Base class for checkbox inputs (boolean / radio / multiselect).
See subclasses such as `CheckboxInputBool`.

Note: this is just the input without a label. When creating a new customized
checkbox, subclass `FormFieldCheckbox` rather than this.
*/
class CheckboxInput extends ei.MixElemValued(eff.MixFormFieldElem(ei.Input)) {
  static {dr.reg(this)}

  // TODO consider using `A.ssrDisabled` and enabling on client.
  get A() {return A.type(`checkbox`).cls(`invis`)}

  init(props) {return E(this, this.A.with(props)).idDefault()}

  connectedCallback() {
    super.connectedCallback()
    this.onclick = this.onClick
  }

  /*
  Allows `.readOnly` to actually prevent changes, which is not natively
  supported by `<input type=checkbox>`.
  */
  onClick(eve) {if (this.readOnly) a.eventKill(eve)}

  /*
  Called by `MixExecable`, which may be added by subclasses.

  For temporary disabling, we prefer `.readOnly` over `.disabled` because it
  preserves focus. At the time of writing, `<input type=checkbox>` does
  natively support the `.readOnly` property linked with the `readonly` boolean
  attribute, but this property DOES NOT actually disable the input. We use
  custom `.onclick` to actually make this work.
  */
  setDisabled(val) {this.readOnly = a.laxBool(val)}

  /*
  Suppress the default input behavior. Multi-select checkboxes and boolean
  checkboxes have different behaviors which must be implemented differently by
  subclasses or parent fieldsets. See subclasses.
  */
  addFormValue() {}

  idDefault() {
    this.id ||= a.laxStr(this.field()?.getId())
    return this
  }
}

class CheckboxLabel extends eff.FormFieldLabel {
  static {dr.reg(this)}
  get A() {return A.cls(`form-choice-label`)}

  // Override in subclass if necessary.
  init(props, ...chi) {
    return E(
      this,
      this.A.with(props),
      CheckboxIcon(),
      CheckboxText(...chi),
    ).forDefault()
  }

  initFromSpec(src) {
    src = a.toInst(src, d.UiField)
    return this.init(undefined, src.getTitleLong())
  }

  setText(val) {this.findDesc(isText).textContent = a.renderLax(val)}
}

function CheckboxIcon() {return E(`span`, A.cls(`form-choice-icon`))}

function CheckboxText(...chi) {
  return E(`span`, A.cls(`form-choice-text`).set(`data-role`, `text`), ...chi)
}

function isText(val) {return val?.dataset.role === `text`}

// iOS-style left-right switch.
export class CheckboxLabelToggle extends CheckboxLabel {
  static {dr.reg(this)}
  get A() {return A.cls(`toggle-label`)}

  init(props, ...chi) {
    return E(
      this,
      this.A.with(props),
      E(`span`, A.cls(`toggle-bullet-body`),
        E(`span`, A.cls(`toggle-bullet`)),
      ),
      CheckboxText(...chi),
    ).forDefault()
  }
}

/*
Represents a boolean checkbox. Note that the native HTML/DOM API does NOT
support treating checkboxes as booleans when submitting a form. In the native
form API, checkboxes are normally used for multiselects. To actually submit
booleans, use our `Form` element, which collects values from inputs by calling
`.addFormValue` on each. See `CheckboxInputBool..addFormValue`.
*/
export class FormFieldBool extends FormFieldCheckbox {
  static {dr.reg(this)}
  get Input() {return CheckboxInputBool}

  initFromSpec(src) {
    src = a.toInst(src, d.UiField)

    return E(this, A,
      new this.Input(this).init({
        name: src.name,
        checked: src.value,
        value: `true`,
      }),
      new this.Label(this).initFromSpec(src),
    )
  }
}

class CheckboxInputBool extends CheckboxInput {
  static {dr.reg(this)}

  /*
  Technical note. Usually it's better to use `undefined` than `null`. In this
  particular case, we use `null` for better compatibility with `JSON.stringify`
  and REST PATCH semantics. When encoding dicts, `JSON.stringify` omits any
  fields whose value is `undefined`. This means when submitting the resulting
  form body via JSON, those fields would not be sent to the backend, which is
  often incorrect. Using `null` for field values avoids that problem.
  */
  getValue() {return this.indeterminate ? null : a.reqBool(this.checked)}

  setValue(val) {
    a.optBool(val)
    this.checked = !!val
    this.indeterminate = a.isNil(val)
    return this
  }

  addFormValue(tar) {
    a.reqDict(tar)
    const name = this.name
    if (!a.optStr(name)) return
    tar[name] = this.getValue()
  }
}

// Base class for boolean toggles that perform an action immediately.
export class FormFieldBoolExecable extends em.MixExecable(FormFieldBool) {
  connectedCallback() {
    super.connectedCallback()
    this.onchange = this.onChange
  }

  async onChange(eve) {
    a.eventKill(eve)
    const inp = this.input()
    const val = inp.checked
    if (!await this.exec(eve)) inp.checked = !val
  }
}

/*
Intended for multiselect-style usage, which is the default treatment
of checkboxes in the HTML/DOM form API.
*/
export class FormFieldCheckboxMulti extends FormFieldCheckbox {
  static {dr.reg(this)}
  get Input() {return CheckboxInputMulti}
}

class CheckboxInputMulti extends CheckboxInput {
  static {dr.reg(this)}

  // Invoked by `Form` when building form body for JSON.
  addFormValue(tar) {
    a.reqDict(tar)
    if (!this.checked) return

    const name = this.name
    if (!a.optStr(name)) return

    const list = tar[name] ??= []
    list.push(this.getValue())
  }
}

/*
Represents a ternary checkbox. Note that the native HTML/DOM API does NOT
support treating checkboxes as booleans when submitting a form. In the native
form API, checkboxes are normally used for multiselects. To actually submit
booleans, use our `Form` element, which collects values from inputs by calling
`.addFormValue` on each. See `CheckboxInputBool..addFormValue`.
*/
export class FormFieldTer extends eff.FormField {
  static {dr.reg(this)}

  initFromSpec(src) {
    src = a.toInst(src, d.UiField)
    const name = src.name
    const value = terEncode(src.value)

    return E(this, this.A,
      new this.Label(this).initFromSpec(src),
      eir.RadioGroup({class: `--hor --small`}, eir.RadioPairs(this.ctx, [
        {name, value: `false`, checked: value === `false`, title: s.CancelRounded(), class: `fg-error`},
        {name, value: `null`, checked: value === `null`, title: s.Circle(), class: `fg-graphite`},
        {name, value: `true`, checked: value === `true`, title: s.Success(), class: `fg-success`},
      ])),
    )
  }

  getValue() {
    return terDecode(a.find(this.elements, getChecked)?.value)
  }

  setValue(val) {
    val = terEncode(val)
    for (const tar of this.elements) tar.checked = tar.value === val
    return this
  }

  addFormValue(tar) {
    a.reqDict(tar)

    for (const src of this.elements) {
      if (src.checked) {
        const name = src.name
        if (a.isValidStr(name)) tar[name] = terDecode(src.value)
      }
    }
  }
}

function getChecked(val) {return val?.checked}

function terEncode(val) {
  if (a.isNil(val)) return `null`
  if (val === false) return `false`
  if (val === true) return `true`
  throw TypeError(`unable to ternary-encode ${a.show(val)}`)
}

function terDecode(val) {
  if (val === `null`) return null
  if (val === `false`) return false
  if (val === `true`) return true
  throw TypeError(`unable to ternary-decode ${a.show(val)}`)
}
